import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FFmpegService } from '../ffmpeg-service';
import { burnSubtitlesSequential, isWebCodecsSupported } from '../webcodecs-subtitle-burner';
import { Upload, FileAudio, AlertCircle, CheckCircle, Loader2, CloudUpload, Zap, Flame, Pencil, Trash2, Plus, Download, Clock, Search, Replace, RefreshCw } from 'lucide-react';
import {
    getR2UploadUrl,
    reencodeVideo,
    initiateMultipartUpload,
    getMultipartUploadUrls,
    completeMultipartUpload,
    checkReplicateStatus,
    transcribeAudioConstance,
    callGeminiConstance,
    deleteR2File,
    withRetry
} from '../services/firebase-api';
import { uploadFileToR2 } from '../services/upload-manager';

export const ConstancePage: React.FC = () => {
    const [isFfmpegLoaded, setIsFfmpegLoaded] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    // Audio State
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const [r2AudioUrl, setR2AudioUrl] = useState<string | null>(null);

    // Video State
    const [r2VideoUrl, setR2VideoUrl] = useState<string | null>(null);

    // Upload State
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadType, setUploadType] = useState<'audio' | 'video' | null>(null);

    // New Re-encode State
    const [isReencoding, setIsReencoding] = useState(false);
    const [lightVideoUrl, setLightVideoUrl] = useState<string[]>([]);

    // Blob Preview State (show video immediately while optimizing)
    const [blobPreviewUrl, setBlobPreviewUrl] = useState<string | null>(null);
    const [isOptimizing, setIsOptimizing] = useState(false);

    // Dual export state (server + web)
    const [serverProgress, setServerProgress] = useState<{ progress: number; message: string } | null>(null);
    const [webProgress, setWebProgress] = useState<{ progress: number; message: string; fps?: number } | null>(null);
    const [serverVideoUrl, setServerVideoUrl] = useState<string | null>(null);
    const [webVideoUrl, setWebVideoUrl] = useState<string | null>(null);
    const cancelBurnRef = useRef(false);
    const videoRef = useRef<HTMLVideoElement>(null);

    // Chunk manager state for client-side merge
    interface ChunkStatus {
        index: number;
        serverStatus: 'pending' | 'downloading' | 'encoding' | 'complete' | 'failed';
        serverProgress: number;
        url: string | null;
        localStatus: 'waiting' | 'downloading' | 'downloaded' | 'failed';
        localProgress: number;
        size: number;
    }
    const [chunks, setChunks] = useState<ChunkStatus[]>([]);
    const [isMerging, setIsMerging] = useState(false);
    const chunksRef = useRef<ChunkStatus[]>([]);
    const downloadingRef = useRef<Set<number>>(new Set());
    const lastJobIdRef = useRef<string | null>(null);

    // Client-side merge: download chunk from R2, write to FFmpeg FS
    const downloadChunkToFS = useCallback(async (chunkIndex: number, chunkUrl: string) => {
        if (downloadingRef.current.has(chunkIndex)) return;
        downloadingRef.current.add(chunkIndex);
        try {
            setChunks(prev => prev.map((c, i) => i === chunkIndex ? { ...c, localStatus: 'downloading' as const, localProgress: 0 } : c));
            setLogs(prev => [...prev.filter(l => !l.startsWith(`📥 Chunk ${chunkIndex}`)), `📥 Chunk ${chunkIndex}: Téléchargement...`]);

            const resp = await fetch(chunkUrl);
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

            const reader = resp.body?.getReader();
            const contentLength = parseInt(resp.headers.get('content-length') || '0', 10);
            const chunks_data: Uint8Array[] = [];
            let received = 0;

            if (reader) {
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    chunks_data.push(value);
                    received += value.length;
                    const pct = contentLength > 0 ? Math.round((received / contentLength) * 100) : 0;
                    setChunks(prev => prev.map((c, i) => i === chunkIndex ? { ...c, localProgress: pct } : c));
                }
            }

            // Combine into single Uint8Array
            const totalLength = chunks_data.reduce((acc, arr) => acc + arr.length, 0);
            const combined = new Uint8Array(totalLength);
            let offset = 0;
            for (const arr of chunks_data) {
                combined.set(arr, offset);
                offset += arr.length;
            }

            // Write to FFmpeg FS
            const ffmpeg = await FFmpegService.getInstance();
            await ffmpeg.writeFile(`chunk_${String(chunkIndex).padStart(3, '0')}.mp4`, combined);

            setChunks(prev => prev.map((c, i) => i === chunkIndex ? { ...c, localStatus: 'downloaded' as const, localProgress: 100, size: totalLength } : c));
            console.log(`[ChunkManager] Chunk ${chunkIndex} downloaded: ${(totalLength / 1024 / 1024).toFixed(1)} MB`);
        } catch (err: any) {
            console.error(`[ChunkManager] Chunk ${chunkIndex} download failed:`, err);
            setChunks(prev => prev.map((c, i) => i === chunkIndex ? { ...c, localStatus: 'failed' as const } : c));
            setLogs(prev => [...prev, `❌ Chunk ${chunkIndex}: ${err.message}`]);
        } finally {
            downloadingRef.current.delete(chunkIndex);
        }
    }, []);

    // Retry a failed chunk download
    const retryChunkDownload = useCallback((chunkIndex: number) => {
        const chunk = chunksRef.current[chunkIndex];
        if (!chunk) return;

        if (chunk.url) {
            // Has URL → retry/force download
            setChunks(prev => prev.map((c, i) => i === chunkIndex ? { ...c, localStatus: 'waiting' as const, localProgress: 0 } : c));
            downloadingRef.current.delete(chunkIndex); // Clear any stuck state
            downloadChunkToFS(chunkIndex, chunk.url);
            setLogs(prev => [...prev, `🔄 Chunk ${chunkIndex}: Relancement du téléchargement...`]);
        } else if (chunk.serverStatus === 'failed') {
            setLogs(prev => [...prev, `⚠️ Chunk ${chunkIndex}: Le serveur a échoué. Relancez l'export pour ce chunk.`]);
        } else if (chunk.serverStatus === 'pending' || chunk.serverStatus === 'encoding') {
            setLogs(prev => [...prev, `⏳ Chunk ${chunkIndex}: En cours côté serveur (${chunk.serverStatus}). Patientez...`]);
        }
    }, [downloadChunkToFS]);

    // Client-side FFmpeg concat merge
    const performClientMerge = useCallback(async (totalChunks: number) => {
        setIsMerging(true);
        setServerProgress({ progress: 90, message: '🔗 Assemblage local (FFmpeg.wasm)...' });
        try {
            const ffmpeg = await FFmpegService.getInstance();

            // Create concat list
            const concatContent = Array.from({ length: totalChunks }, (_, i) =>
                `file 'chunk_${String(i).padStart(3, '0')}.mp4'`
            ).join('\n');
            await ffmpeg.writeFile('concat_chunks.txt', new TextEncoder().encode(concatContent));

            // FFmpeg concat -c copy (instant, no re-encoding)
            await ffmpeg.exec([
                '-f', 'concat', '-safe', '0',
                '-i', 'concat_chunks.txt',
                '-c', 'copy', '-movflags', '+faststart',
                '-y', 'merged_output.mp4'
            ]);

            // Read result
            const outputData = await ffmpeg.readFile('merged_output.mp4') as Uint8Array;
            const blob = new Blob([outputData.slice().buffer], { type: 'video/mp4' });
            const blobUrl = URL.createObjectURL(blob);

            setServerVideoUrl(blobUrl);
            setServerProgress(null);
            setLogs(prev => [...prev, `✅ Assemblage local terminé! (${(blob.size / 1024 / 1024).toFixed(1)} MB)`]);

            // Auto-download
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = 'video_cloud.mp4';
            a.click();

            // Cleanup FFmpeg FS
            for (let i = 0; i < totalChunks; i++) {
                await ffmpeg.deleteFile(`chunk_${String(i).padStart(3, '0')}.mp4`).catch(() => { });
            }
            await ffmpeg.deleteFile('concat_chunks.txt').catch(() => { });
            await ffmpeg.deleteFile('merged_output.mp4').catch(() => { });

        } catch (err: any) {
            console.error('[ChunkManager] Merge failed:', err);
            setLogs(prev => [...prev, `❌ Merge local: ${err.message}`]);
            setServerProgress(null);
        } finally {
            setIsMerging(false);
        }
    }, []);

    // Keep ref in sync
    useEffect(() => { chunksRef.current = chunks; }, [chunks]);

    // Auto-trigger merge when all chunks downloaded
    useEffect(() => {
        if (chunks.length === 0 || isMerging) return;
        const allDownloaded = chunks.every(c => c.localStatus === 'downloaded');
        const anyComplete = chunks.some(c => c.serverStatus === 'complete');
        if (allDownloaded && anyComplete && !serverVideoUrl) {
            performClientMerge(chunks.length);
        }
    }, [chunks, isMerging, serverVideoUrl, performClientMerge]);

    const [error, setError] = useState<string | null>(null);
    const [logs, setLogs] = useState<string[]>([]);
    const [subtitleUrl, setSubtitleUrl] = useState<string | null>(null);

    // Subtitle segments state for editing
    const [subtitleSegments, setSubtitleSegments] = useState<{ text: string; start: number; end: number }[]>([]);
    const [editingSubId, setEditingSubId] = useState<number | null>(null);
    const [showSubEditor, setShowSubEditor] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [replaceQuery, setReplaceQuery] = useState('');
    const [showReplace, setShowReplace] = useState(false);
    const [activeSubIndex, setActiveSubIndex] = useState<number>(-1);
    const subListRef = useRef<HTMLDivElement>(null);

    // VTT Helper
    const formatTime = (seconds: number) => {
        const date = new Date(0);
        date.setSeconds(seconds);
        const line = date.toISOString().substr(11, 8);
        const ms = (seconds % 1).toFixed(3).substr(2);
        return `${line}.${ms}`;
    };

    const generateVtt = (segments: any[]) => {
        let vtt = "WEBVTT\n\n";
        segments.forEach((seg: any) => {
            const start = formatTime(seg.start);
            const end = formatTime(seg.end);
            vtt += `${start} --> ${end}\n${seg.text}\n\n`;
        });
        return URL.createObjectURL(new Blob([vtt], { type: 'text/vtt' }));
    };

    // Regenerate VTT whenever segments change
    const regenerateVtt = useCallback((segs: { text: string; start: number; end: number }[]) => {
        if (segs.length === 0) return;
        const url = generateVtt(segs);
        setSubtitleUrl(url);
    }, []);

    // Subtitle editing handlers
    const handleSubTextChange = (index: number, newText: string) => {
        setSubtitleSegments(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], text: newText };
            regenerateVtt(updated);
            return updated;
        });
    };

    const handleSubTimingChange = (index: number, field: 'start' | 'end', value: number) => {
        setSubtitleSegments(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            regenerateVtt(updated);
            return updated;
        });
    };

    const handleDeleteSub = (index: number) => {
        setSubtitleSegments(prev => {
            const updated = prev.filter((_, i) => i !== index);
            regenerateVtt(updated);
            return updated;
        });
    };

    const handleAddSub = (afterIndex: number) => {
        setSubtitleSegments(prev => {
            const ref = prev[afterIndex];
            const nextRef = prev[afterIndex + 1];
            const newStart = ref ? ref.end + 0.1 : 0;
            const newEnd = nextRef ? nextRef.start - 0.1 : newStart + 2;
            const newSeg = { text: 'New subtitle', start: newStart, end: Math.max(newEnd, newStart + 0.5) };
            const updated = [...prev.slice(0, afterIndex + 1), newSeg, ...prev.slice(afterIndex + 1)];
            regenerateVtt(updated);
            return updated;
        });
    };

    const handleSeekTo = (time: number) => {
        if (videoRef.current) {
            videoRef.current.currentTime = time;
            videoRef.current.play().catch(() => { });
        }
    };

    const handleExportSrt = () => {
        if (subtitleSegments.length === 0) return;
        let srt = '';
        subtitleSegments.forEach((seg, i) => {
            const fmtTime = (s: number) => {
                const h = Math.floor(s / 3600).toString().padStart(2, '0');
                const m = Math.floor((s % 3600) / 60).toString().padStart(2, '0');
                const sec = Math.floor(s % 60).toString().padStart(2, '0');
                const ms = Math.round(((s % 1) * 1000)).toString().padStart(3, '0');
                return `${h}:${m}:${sec},${ms}`;
            };
            srt += `${i + 1}\n${fmtTime(seg.start)} --> ${fmtTime(seg.end)}\n${seg.text}\n\n`;
        });
        const blob = new Blob([srt], { type: 'text/plain' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'subtitles.srt';
        a.click();
    };

    // Track active subtitle from video playback
    useEffect(() => {
        const video = videoRef.current;
        if (!video || subtitleSegments.length === 0) return;
        const onTimeUpdate = () => {
            const t = video.currentTime;
            const idx = subtitleSegments.findIndex(s => t >= s.start && t <= s.end);
            if (idx !== activeSubIndex) {
                setActiveSubIndex(idx);
                // Auto-scroll to active subtitle
                if (idx >= 0 && subListRef.current) {
                    const el = subListRef.current.children[idx] as HTMLElement;
                    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                }
            }
        };
        video.addEventListener('timeupdate', onTimeUpdate);
        return () => video.removeEventListener('timeupdate', onTimeUpdate);
    }, [subtitleSegments, activeSubIndex]);
    useEffect(() => {
        const loadFfmpeg = async () => {
            try {
                addLog('Chargement de FFmpeg...');
                await FFmpegService.getInstance();
                setIsFfmpegLoaded(true);
                addLog('FFmpeg chargé avec succès !');
            } catch (e: any) {
                setError(`Erreur chargement FFmpeg: ${e.message}`);
                addLog(`Erreur: ${e.message}`);
            }
        };
        loadFfmpeg();
    }, []);

    const addLog = (msg: string) => {
        setLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${msg}`]);
    };

    const onDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const droppedFile = e.dataTransfer.files[0];
            if (droppedFile.type.startsWith('video/')) {
                setFile(droppedFile);
                setError(null);
                setAudioUrl(null);
                setR2AudioUrl(null);
                setR2VideoUrl(null);
                setLightVideoUrl([]);
                addLog(`Fichier reçu: ${droppedFile.name} (${(droppedFile.size / 1024 / 1024).toFixed(2)} MB)`);
            } else {
                setError("Veuillez déposer un fichier vidéo.");
            }
        }
    }, []);

    const onDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleExtract = async () => {
        if (!file || !isFfmpegLoaded) return;

        setIsProcessing(true);
        setProgress(0);
        setError(null);
        setAudioUrl(null);
        addLog('Début de l\'extraction audio...');

        try {
            // Utilisation de la méthode pour gros fichiers (via WORKERFS) car plus robuste
            const blob = await FFmpegService.extractAudioFromLargeFile(file, (ratio) => {
                setProgress(Math.round(ratio * 100));
            });

            const url = URL.createObjectURL(blob);
            setAudioUrl(url);
            addLog('Extraction terminée avec succès !');
        } catch (e: any) {
            console.error(e);
            setError(`Erreur lors de l'extraction: ${e.message}`);
            addLog(`Erreur extraction: ${e.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    const performUpload = async (fileToUpload: File | Blob, type: 'audio' | 'video'): Promise<string> => {
        return uploadFileToR2({
            file: fileToUpload,
            type,
            onProgress: setUploadProgress,
            logFn: addLog
        });
    };

    const handleUploadToR2 = async (type: 'audio' | 'video') => {
        if (!file) return;
        if (type === 'audio' && !audioUrl) return;

        setIsUploading(true);
        setUploadType(type);
        setUploadProgress(0);
        addLog(`Démarrage de l'upload R2 (${type})...`);

        try {
            // Prepare content
            let blob: Blob | File;
            if (type === 'audio' && audioUrl) {
                const response = await fetch(audioUrl);
                blob = await response.blob();
            } else {
                blob = file;
            }

            const publicUrl = await performUpload(blob, type);

            if (type === 'audio') {
                setR2AudioUrl(publicUrl);
            } else {
                setR2VideoUrl(publicUrl);
            }
            addLog(`Upload ${type} terminé ! URL: ${publicUrl}`);
            setIsUploading(false);
            setUploadType(null);

        } catch (e: any) {
            console.error(e);
            setError(`Erreur upload R2: ${e.message}`);
            addLog(`Erreur upload: ${e.message}`);
            setIsUploading(false);
            setUploadType(null);
        }
    };

    const handleReencode = async (videoUrl?: string) => {
        const urlToUse = videoUrl || r2VideoUrl;
        if (!urlToUse) return;

        setIsReencoding(true);
        setLightVideoUrl([]);
        addLog('Lancement du ré-encodage "Lighter" sur la vidéo source...');

        try {
            // 1. Launch Async Job
            const result = await reencodeVideo({ videoUrl: urlToUse });
            const data = (result.data as any);

            if (data.success && data.predictionId) {
                const predId = data.predictionId;
                addLog(`Job lancé ! ID: ${predId}. En attente de traitement...`);

                // 2. Poll Status
                const POLLING_INTERVAL = 3000; // 3 sec

                const poll = async () => {
                    try {
                        const statusRes = await checkReplicateStatus({ predictionId: predId });
                        const statusData = (statusRes.data as any);

                        addLog(`Status Replicate: ${statusData.status}`);

                        if (statusData.status === 'succeeded') {
                            setLightVideoUrl(statusData.urls);
                            addLog(`✅ Ré-encodage terminé ! ${statusData.urls.length} fichier(s).`);

                            // Calculate Compression Stats
                            if (statusData.urls.length > 0 && file) {
                                try {
                                    const headRes = await fetch(statusData.urls[0], { method: 'HEAD' });
                                    const newSize = parseInt(headRes.headers.get('content-length') || '0', 10);
                                    if (newSize > 0 && file.size > 0) {
                                        const ratio = (1 - (newSize / file.size)) * 100;
                                        addLog(`📉 Compression: -${ratio.toFixed(1)}% (${(newSize / 1024 / 1024).toFixed(2)} MB)`);
                                    }
                                } catch (err) {
                                    console.warn('Failed to calculate compression stats', err);
                                }
                            }

                            setIsReencoding(false);
                        } else if (statusData.status === 'failed' || statusData.status === 'canceled') {
                            throw new Error(`Job ${statusData.status}: ${statusData.error}`);
                        } else {
                            // Continue polling
                            setTimeout(poll, POLLING_INTERVAL);
                        }
                    } catch (err: any) {
                        console.error('Polling Error', err);
                        setError(`Erreur polling: ${err.message}`);
                        setIsReencoding(false);
                    }
                };

                // Start polling loop
                setTimeout(poll, POLLING_INTERVAL);

            } else {
                throw new Error("Pas de Prediction ID retourné par le serveur");
            }
        } catch (e: any) {
            console.error(e);
            setError(`Erreur lancement ré-encodage: ${e.message}`);
            addLog(`Erreur: ${e.message}`);
            setIsReencoding(false);
        }
    };

    const handleMagicProcess = async () => {
        if (!file || !isFfmpegLoaded) return;

        setIsUploading(true);
        setUploadType('video');
        setUploadProgress(0);
        setError(null);
        setLightVideoUrl([]);
        setR2VideoUrl(null);
        setAudioUrl(null);
        setR2AudioUrl(null);

        // Show blob preview immediately
        const previewUrl = URL.createObjectURL(file);
        setBlobPreviewUrl(previewUrl);
        setIsOptimizing(true);

        addLog('🚀 Démarrage du Workflow Magique (Parallèle)...');

        try {
            // --- BRANCH A: COMPRESSION VIDEO ---
            const videoProcess = async () => {
                addLog('🎥 [Vidéo] Upload vers R2 en cours...');
                const masterUrl = await performUpload(file, 'video');
                setR2VideoUrl(masterUrl);
                addLog(`🎥 [Vidéo] Upload terminé. Optimisation Replicate...`);

                // Re-encode with retry + fallback to master URL
                const MAX_RETRIES = 2;
                const RETRY_DELAY = 15000;
                let replicateSuccess = false;

                for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
                    try {
                        if (attempt > 0) {
                            addLog(`⚠️ [Vidéo] Retry ${attempt}/${MAX_RETRIES} dans ${RETRY_DELAY / 1000}s...`);
                            await new Promise(r => setTimeout(r, RETRY_DELAY));
                        }

                        const result = await reencodeVideo({ videoUrl: masterUrl });
                        const data = (result.data as any);

                        if (!data.success || !data.predictionId) throw new Error("Echec Lancement Replicate");

                        addLog(`🎥 [Vidéo] Job Replicate lancé (${data.predictionId}). Attente...`);

                        // Polling Loop
                        const POLLING_INTERVAL = 3000;

                        while (true) {
                            await new Promise(r => setTimeout(r, POLLING_INTERVAL));
                            const statusRes = await checkReplicateStatus({ predictionId: data.predictionId });
                            const statusData = (statusRes.data as any);

                            if (statusData.status === 'succeeded') {
                                const finalUrls = statusData.urls;
                                setLightVideoUrl(finalUrls);
                                setIsOptimizing(false);


                                replicateSuccess = true;
                                break;
                            } else if (statusData.status === 'failed' || statusData.status === 'canceled') {
                                throw new Error(`Replicate Failed: ${statusData.error}`);
                            }
                        }

                        if (replicateSuccess) break; // exit retry loop
                    } catch (replicateErr: any) {
                        console.error(`[Vidéo] Replicate attempt ${attempt + 1} failed:`, replicateErr);
                        if (attempt < MAX_RETRIES) {
                            addLog(`⚠️ [Vidéo] Replicate échoué: ${replicateErr.message}. Nouvelle tentative...`);
                        } else {
                            addLog(`⚠️ [Vidéo] Replicate échoué après ${MAX_RETRIES + 1} tentatives. Utilisation du fichier Master R2.`);
                        }
                    }
                }

                if (replicateSuccess) {
                    // Keep R2 master alive for export — replicate.delivery URLs expire
                    addLog(`✅ [Vidéo] Optimisation terminée. Master R2 conservé pour l'export.`);
                } else {
                    // Fallback: keep master URL, mark optimization as done (using original)
                    setIsOptimizing(false);
                    addLog(`📦 [Fallback] Le fichier Master R2 reste disponible pour l'export.`);
                }
            };

            // --- BRANCH B: INTELLIGENCE AUDIO ---
            const audioProcess = async () => {
                addLog('🎧 [Audio] Extraction MP3 en local...');
                // Extract
                const blob = await FFmpegService.extractAudioFromLargeFile(file, (r) => {
                    // Local audio progress (optional log)
                });
                const mp3File = new File([blob], "extract.mp3", { type: 'audio/mpeg' });
                const audioObjUrl = URL.createObjectURL(blob);
                setAudioUrl(audioObjUrl);

                addLog('🎧 [Audio] Upload MP3 vers R2...');
                const mp3Url = await performUpload(mp3File, 'audio'); // Reuse robust upload
                setR2AudioUrl(mp3Url);

                addLog('🧠 [Intelligence] Transcription Whisper (Force EN)...');

                // Generate a unique jobId for async Firestore pattern
                const transcriptionJobId = `transcription_${Date.now()}_${Math.random().toString(36).substring(7)}`;

                // Helper: poll Firestore for transcription results (survives HTTP drops)
                const pollFirestoreForResult = async (): Promise<any> => {
                    const { doc, onSnapshot, getFirestore } = await import('firebase/firestore');
                    const db = getFirestore();
                    const jobRef = doc(db, 'transcription-jobs', transcriptionJobId);

                    return new Promise((resolve, reject) => {
                        const unsubscribe = onSnapshot(jobRef, (snapshot) => {
                            if (!snapshot.exists()) return;
                            const data = snapshot.data();
                            if (data?.status === 'complete') {
                                unsubscribe();
                                addLog('✅ Résultats récupérés via Firestore!');
                                resolve({ data: { subtitles: data.subtitles, language: data.language } });
                            } else if (data?.status === 'error') {
                                unsubscribe();
                                reject(new Error(data.error || 'Transcription failed'));
                            } else if (data?.status === 'processing') {
                                addLog('⏳ Transcription en cours sur le serveur...');
                            }
                        });
                    });
                };

                // Fire-and-forget: send HTTP call ONCE (no retry), poll Firestore immediately
                // Whichever returns first wins — avoids duplicate Replicate jobs
                addLog('🔄 Transcription lancée. Écoute Firestore en parallèle...');
                const httpPromise = transcribeAudioConstance({
                    audioUrl: mp3Url,
                    language: 'en',
                    response_format: 'verbose_json',
                    jobId: transcriptionJobId
                }).catch((err: any) => {
                    console.log('[Constance] HTTP call dropped (normal for long files):', err.message);
                    return null; // Don't throw — Firestore will deliver the result
                });

                const transRes = await Promise.race([
                    httpPromise.then(r => r ? r : pollFirestoreForResult()),
                    pollFirestoreForResult()
                ]);
                console.log('Transcription Response:', transRes.data);
                const data = transRes.data as any;

                // Flexible parsing (handle various Replicate/Whisper output formats)
                let transcriptText = data.text || data.transcription || (data.output && data.output.text) || JSON.stringify(data);

                if (!transcriptText) {
                    // Fallback: reconstruct from subtitles if available
                    if (data.subtitles && Array.isArray(data.subtitles)) {
                        transcriptText = data.subtitles.map((s: any) => s.text).join(' ');
                    } else {
                        throw new Error("Réponse de transcription vide");
                    }
                }

                // Generate Subtitles (VTT) from refined subtitles or segments
                const segments = data.subtitles || data.segments;
                if (segments) {
                    setSubtitleSegments(segments);
                    const vttUrl = generateVtt(segments);
                    setSubtitleUrl(vttUrl);
                    setShowSubEditor(true);
                    addLog(`📜 ${segments.length} sous-titres VTT générés (Lexique LVMH appliqué).`);
                }

                addLog(`📝 [Transcript] ${transcriptText.substring(0, 50)}...`);

                addLog('💎 [Gemini] Analyse avec Lexique LVMH...');
                const geminiRes = await callGeminiConstance({
                    model: 'gemini-3.5-flash',
                    contents: [{
                        role: 'user',
                        parts: [{ text: `Analyze this transcript in the context of LVMH. identify key themes, luxury terminology, and sentiment. Transcript: ${transcriptText}` }]
                    }]
                });
                const analysis = (geminiRes.data as any).candidates[0].content.parts[0].text;
                addLog(`🧐 [Analyse] Terminée.`);
                console.log("GEMINI ANALYSIS:", analysis);

                // Cleanup MP3 (Best effort)
                try {
                    // await deleteR2File({ key: ... }); 
                } catch (cleanupErr) {
                    console.warn("Cleanup failed (non-fatal):", cleanupErr);
                }
            };

            // LAUNCH PARALLEL — audioProcess is non-fatal (video continues even if transcription fails)
            const safeAudioProcess = async () => {
                try {
                    await audioProcess();
                } catch (audioErr: any) {
                    console.error('Audio process failed (non-fatal):', audioErr);
                    addLog(`⚠️ [Audio] Transcription échouée (non-bloquant): ${audioErr.message}`);
                    addLog(`💡 La vidéo optimisée reste disponible sans sous-titres.`);
                }
            };
            await Promise.all([videoProcess(), safeAudioProcess()]);

            addLog('🎉 WORKFLOW MAGIQUE TERMINÉ !');
            setIsUploading(false);

        } catch (e: any) {
            console.error(e);
            setError(`Erreur Workflow: ${e.message}`);
            addLog(`❌ Erreur: ${e.message}`);
            setIsUploading(false);
            setIsReencoding(false);
        }
    };

    return (
        <div className="h-screen bg-white text-black font-mono border-8 border-black overflow-hidden flex">
            {/* Main Content */}
            <div className="flex-1 overflow-y-auto p-8">
                <div className="max-w-4xl mx-auto space-y-8">

                    <header className="border-b-4 border-black pb-4 flex justify-between items-end">
                        <div>
                            <h1 className="text-4xl font-black uppercase tracking-tighter">
                                Constance Workflow
                            </h1>
                            <p className="text-gray-600 mt-1 uppercase text-xs tracking-widest">
                                Optimization & Intelligence v2.0
                            </p>
                        </div>
                        <div className="text-right">
                            <div className="text-xs font-bold uppercase">System Status</div>
                            <div className="text-sm">
                                FFmpeg: {isFfmpegLoaded ? <span className="bg-black text-white px-1">ONLINE</span> : <span className="bg-gray-200 px-1">LOADING</span>}
                            </div>
                        </div>
                    </header>

                    {/* ZONE DE DROP */}
                    <div
                        onDrop={onDrop}
                        onDragOver={onDragOver}
                        className={`
            border-4 border-dashed rounded-none p-12 text-center transition-all cursor-pointer
            ${file ? 'border-black bg-gray-100' : 'border-gray-300 hover:border-black hover:bg-gray-50'}
          `}
                    >
                        {!file && (
                            <div className="flex flex-col items-center gap-4 opacity-50">
                                <CloudUpload size={48} strokeWidth={1} />
                                <div className="text-xl font-bold uppercase">Drop Video Master here</div>
                                <div className="text-xs text-gray-400">MP4, MOV, WEBM supported</div>
                            </div>
                        )}

                        {file && !isProcessing && !isUploading && (
                            <div className="animate-in fade-in space-y-6">
                                <div className="text-lg font-bold border-b border-black pb-2 inline-block">
                                    {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                                </div>

                                <div className="flex justify-center">
                                    <button
                                        onClick={handleMagicProcess}
                                        className="bg-black text-white text-xl font-bold py-4 px-12 uppercase tracking-widest hover:bg-gray-800 transition-colors border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-y-1 hover:shadow-none active:translate-y-1 active:shadow-none"
                                    >
                                        Start Magic Process
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500 max-w-md mx-auto">
                                    Automatically triggers: R2 Upload + Replicate Optimization + Whisper Analysis + Auto-Cleanup.
                                </p>
                            </div>
                        )}

                        {/* VISUALISATION DES PROGRESSIONS */}
                        {isUploading && (
                            <div className="mt-8 max-w-md mx-auto text-left border border-black p-4 bg-white relative overflow-hidden">
                                <div className="absolute top-0 left-0 bottom-0 bg-gray-200 -z-10 transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                                <div className="flex justify-between items-center font-bold">
                                    <span>UPLOADING TO R2...</span>
                                    <span>{uploadProgress}%</span>
                                </div>
                            </div>
                        )}

                        {/* ETAT RE-ENCODE */}
                        {isReencoding && (
                            <div className="mt-8 border border-black p-4 flex items-center justify-center gap-3 animate-pulse">
                                <Loader2 className="animate-spin" />
                                <span className="font-bold uppercase">Processing...</span>
                            </div>
                        )}

                        {/* RESULTATS */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12 text-left">

                            {/* RESULTAT VIDEO (FULL WIDTH) - Show blob OR optimized */}
                            {(blobPreviewUrl || lightVideoUrl.length > 0) && (
                                <div className="md:col-span-2 border-4 border-black p-4 bg-gray-50">
                                    <h4 className="font-black uppercase mb-4 flex items-center gap-2">
                                        <Zap size={20} className="fill-black" />
                                        {lightVideoUrl.length > 0 ? 'Optimized Video + Subtitles' : 'Video Preview + Subtitles'}
                                        {isOptimizing && (
                                            <span className="ml-2 px-2 py-1 bg-yellow-400 text-black text-xs font-bold uppercase rounded-sm animate-pulse flex items-center gap-1">
                                                <Loader2 size={12} className="animate-spin" />
                                                Conversion en cours...
                                            </span>
                                        )}
                                    </h4>
                                    {(lightVideoUrl.length > 0 ? lightVideoUrl : [blobPreviewUrl]).filter(Boolean).map((url, idx) => (
                                        <div key={`${idx}-${subtitleUrl ? 'sub' : 'nosub'}`} className="space-y-4 relative">
                                            <video
                                                ref={idx === 0 ? videoRef : undefined}
                                                controls
                                                src={url!}
                                                className="w-full border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]"
                                            >
                                                {subtitleUrl && <track kind="subtitles" src={subtitleUrl} label="English" srcLang="en" default />}
                                            </video>

                                            {/* Dual Export Progress Overlay */}
                                            {(serverProgress || webProgress) && (
                                                <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center text-white p-4">
                                                    <div className="w-full max-w-md space-y-4">
                                                        {/* Server Progress */}
                                                        {serverProgress && (
                                                            <div>
                                                                <div className="flex items-center justify-between mb-1">
                                                                    <span className="text-sm font-bold">☁️ Cloud (MP4)</span>
                                                                    <span className="text-xl font-black">{serverProgress.progress}%</span>
                                                                </div>
                                                                <div className="w-full h-3 bg-gray-700 rounded-full overflow-hidden">
                                                                    <div className="h-full bg-purple-500 transition-all duration-200" style={{ width: `${serverProgress.progress}%` }} />
                                                                </div>
                                                                <div className="text-xs font-mono mt-1 text-gray-300">{serverProgress.message}</div>
                                                            </div>
                                                        )}
                                                        {serverVideoUrl && !serverProgress && (
                                                            <div className="text-sm text-green-400 font-bold">☁️ Cloud: ✅ Terminé!</div>
                                                        )}

                                                        {/* Web Progress */}
                                                        {webProgress && (
                                                            <div>
                                                                <div className="flex items-center justify-between mb-1">
                                                                    <span className="text-sm font-bold">🌐 Web (MP4)</span>
                                                                    <span className="text-xl font-black">{webProgress.progress}%</span>
                                                                </div>
                                                                <div className="w-full h-3 bg-gray-700 rounded-full overflow-hidden">
                                                                    <div className="h-full bg-blue-500 transition-all duration-200" style={{ width: `${webProgress.progress}%` }} />
                                                                </div>
                                                                <div className="text-xs font-mono mt-1 text-gray-300">{webProgress.message}</div>
                                                                {webProgress.fps && (
                                                                    <div className="text-xs text-gray-400 mt-1">~{webProgress.fps.toFixed(1)} fps</div>
                                                                )}
                                                            </div>
                                                        )}
                                                        {webVideoUrl && !webProgress && (
                                                            <div className="text-sm text-green-400 font-bold">🌐 Web: ✅ Terminé!</div>
                                                        )}
                                                    </div>

                                                    <button
                                                        onClick={() => {
                                                            cancelBurnRef.current = true;
                                                            setServerProgress(null);
                                                            setWebProgress(null);
                                                            setIsProcessing(false);
                                                            setLogs(prev => [...prev, `❌ Export annulé par l'utilisateur`]);
                                                        }}
                                                        className="mt-6 px-6 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg transition-colors"
                                                    >
                                                        ✕ Annuler
                                                    </button>
                                                </div>
                                            )}
                                            <div className="flex justify-between items-center bg-gray-100 p-2 rounded-lg">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-bold bg-black text-white px-2 py-1 uppercase rounded-sm">
                                                        {subtitleUrl ? "Subtitles Active" : "No Subtitles"}
                                                    </span>
                                                    {lightVideoUrl.length > 0 && (
                                                        <span className="text-xs font-bold bg-green-600 text-white px-2 py-1 uppercase rounded-sm">
                                                            ✓ Optimized
                                                        </span>
                                                    )}
                                                    {subtitleUrl && (() => {
                                                        const hasOptimizedUrl = lightVideoUrl.some(u => u && !u.startsWith('blob:'));
                                                        const hasR2Url = r2VideoUrl && !r2VideoUrl.startsWith('blob:');
                                                        const isBlobUrl = url.startsWith('blob:');
                                                        const cloudReady = !isBlobUrl || hasOptimizedUrl || hasR2Url;

                                                        return (
                                                            <button
                                                                disabled={isProcessing}
                                                                onClick={async () => {
                                                                    if (!url || !subtitleUrl) return;

                                                                    setIsProcessing(true);
                                                                    cancelBurnRef.current = false;
                                                                    setServerVideoUrl(null);
                                                                    setWebVideoUrl(null);

                                                                    // Track which exports are still running
                                                                    let serverDone = false;
                                                                    let webDone = false;
                                                                    const checkAllDone = () => {
                                                                        if (serverDone && webDone) {
                                                                            setIsProcessing(false);
                                                                            setServerProgress(null);
                                                                            setWebProgress(null);
                                                                            setTimeout(() => {
                                                                                document.querySelector('[data-burned-result]')?.scrollIntoView({ behavior: 'smooth' });
                                                                            }, 300);
                                                                        }
                                                                    };

                                                                    // ═══════════════════════════════════════
                                                                    // 1. SERVER EXPORT (☁️ Cloud → MP4)
                                                                    // ═══════════════════════════════════════
                                                                    const serverExport = async () => {
                                                                        try {
                                                                            if (!cloudReady) {
                                                                                setLogs(prev => [...prev, `☁️ Cloud: ⚠️ Vidéo non disponible sur R2`]);
                                                                                serverDone = true;
                                                                                checkAllDone();
                                                                                return;
                                                                            }

                                                                            let cloudVideoUrl = r2VideoUrl || url;
                                                                            if (cloudVideoUrl.startsWith('blob:')) {
                                                                                const optimizedUrl = lightVideoUrl.find(u => u && !u.startsWith('blob:'));
                                                                                if (optimizedUrl) cloudVideoUrl = optimizedUrl;
                                                                            }

                                                                            const EXPORT_SERVICE_URL = import.meta.env.VITE_EXPORT_SERVICE_URL || 'https://export-service-400620783493.us-central1.run.app';

                                                                            // ── Smart retry: if chunks exist with failures, retry only failed chunks ──
                                                                            const hasFailedChunks = chunksRef.current.length > 0 &&
                                                                                chunksRef.current.some(c => ['failed', 'error', 'encoding', 'downloading'].includes(c.serverStatus)) &&
                                                                                chunksRef.current.some(c => c.serverStatus === 'complete') &&
                                                                                lastJobIdRef.current;

                                                                            if (hasFailedChunks) {
                                                                                const failedCount = chunksRef.current.filter(c => ['failed', 'error', 'encoding', 'downloading'].includes(c.serverStatus)).length;
                                                                                setLogs(prev => [...prev, `☁️ Cloud: 🔄 Relance de ${failedCount} chunk(s) bloqué(s) (job: ${lastJobIdRef.current})`]);
                                                                                setServerProgress({ progress: 0, message: `Relance ${failedCount} chunks...` });

                                                                                // Reset local status for failed chunks
                                                                                setChunks(prev => prev.map(c =>
                                                                                    ['failed', 'error', 'encoding', 'downloading'].includes(c.serverStatus) ? { ...c, serverStatus: 'pending' as const, serverProgress: 0, url: null, localStatus: 'waiting' as const, localProgress: 0 } : c
                                                                                ));

                                                                                fetch(`${EXPORT_SERVICE_URL}/retry-failed-chunks`, {
                                                                                    method: 'POST',
                                                                                    headers: { 'Content-Type': 'application/json' },
                                                                                    body: JSON.stringify({ jobId: lastJobIdRef.current })
                                                                                }).catch(() => { });

                                                                                // Re-subscribe to Firestore for updates
                                                                                const { doc, onSnapshot, getFirestore } = await import('firebase/firestore');
                                                                                const db = getFirestore();
                                                                                const jobDocRef = doc(db, 'export-jobs', lastJobIdRef.current!);

                                                                                const firestoreResultPromise = new Promise<void>((resolve, reject) => {
                                                                                    const unsubscribe = onSnapshot(jobDocRef, (snapshot) => {
                                                                                        if (!snapshot.exists()) return;
                                                                                        const data = snapshot.data();
                                                                                        const totalChunks = data?.totalChunks || 0;
                                                                                        let chunksDone = 0;
                                                                                        let chunksFailed = 0;

                                                                                        if (totalChunks > 0) {
                                                                                            const updates: Partial<ChunkStatus>[] = [];
                                                                                            for (let ci = 0; ci < totalChunks; ci++) {
                                                                                                const cStatus = data?.[`chunk_${ci}_status`] || 'pending';
                                                                                                const cProgress = data?.[`chunk_${ci}_progress`] || 0;
                                                                                                const cUrl = data?.[`chunk_${ci}_url`] || null;
                                                                                                if (cStatus === 'complete') chunksDone++;
                                                                                                else if (cStatus === 'failed') chunksFailed++;
                                                                                                updates.push({ serverStatus: cStatus as any, serverProgress: cProgress, url: cUrl });
                                                                                                if (cStatus === 'complete' && cUrl) {
                                                                                                    const currentChunk = chunksRef.current[ci];
                                                                                                    if (currentChunk && currentChunk.localStatus === 'waiting') {
                                                                                                        downloadChunkToFS(ci, cUrl);
                                                                                                    }
                                                                                                }
                                                                                            }
                                                                                            setChunks(prev => prev.map((c, i) => ({
                                                                                                ...c, ...(updates[i] || {}),
                                                                                                localStatus: c.localStatus, localProgress: c.localProgress, size: c.size
                                                                                            })));
                                                                                        }
                                                                                        setServerProgress({ progress: Math.round((chunksDone / totalChunks) * 80) + 15, message: `⚙️ ${chunksDone}/${totalChunks} ✅` });
                                                                                        if (chunksDone === totalChunks && totalChunks > 0) { unsubscribe(); resolve(); }
                                                                                        if (chunksFailed > 0 && chunksDone + chunksFailed >= totalChunks) { unsubscribe(); reject(new Error(`${chunksFailed} chunk(s) encore en échec`)); }
                                                                                        if (data?.status === 'failed') { unsubscribe(); reject(new Error(data.error || 'Export failed')); }
                                                                                    });
                                                                                });

                                                                                await firestoreResultPromise;
                                                                                setLogs(prev => [...prev, `☁️ Cloud: ✅ Tous les chunks prêts! Assemblage local...`]);
                                                                                return;
                                                                            }

                                                                            // ── Normal: new export ──
                                                                            const jobId = `export_${Date.now()}`;
                                                                            lastJobIdRef.current = jobId;
                                                                            setLogs(prev => [...prev, `☁️ Cloud: Lancé (job: ${jobId})`]);
                                                                            setServerProgress({ progress: 0, message: 'Préparation...' });

                                                                            // Upload subtitles to R2
                                                                            const subtitleResponse = await fetch(subtitleUrl!);
                                                                            const subtitleBlob = await subtitleResponse.blob();
                                                                            const srtFilename = `subtitles_${Date.now()}.srt`;
                                                                            const uploadResult = await getR2UploadUrl({ fileName: srtFilename, contentType: 'text/plain' });
                                                                            const { uploadUrl, publicUrl: r2SubtitleUrl } = (uploadResult.data as any);

                                                                            await fetch(uploadUrl, {
                                                                                method: 'PUT',
                                                                                headers: { 'Content-Type': 'text/plain' },
                                                                                body: subtitleBlob
                                                                            });

                                                                            setServerProgress({ progress: 5, message: 'Lancement...' });

                                                                            const { doc, onSnapshot, getFirestore } = await import('firebase/firestore');
                                                                            const db = getFirestore();
                                                                            const jobDocRef = doc(db, 'export-jobs', jobId);

                                                                            const firestoreResultPromise = new Promise<void>((resolve, reject) => {
                                                                                const unsubscribe = onSnapshot(jobDocRef, (snapshot) => {
                                                                                    if (!snapshot.exists()) return;
                                                                                    const data = snapshot.data();

                                                                                    const totalChunks = data?.totalChunks || 0;
                                                                                    let chunksDone = 0;
                                                                                    let chunksFailed = 0;
                                                                                    let chunksEncoding = 0;
                                                                                    let totalChunkProgress = 0;

                                                                                    // Initialize chunks array if not yet done
                                                                                    if (totalChunks > 0 && chunksRef.current.length === 0) {
                                                                                        const initial: ChunkStatus[] = Array.from({ length: totalChunks }, (_, i) => ({
                                                                                            index: i,
                                                                                            serverStatus: 'pending' as const,
                                                                                            serverProgress: 0,
                                                                                            url: null,
                                                                                            localStatus: 'waiting' as const,
                                                                                            localProgress: 0,
                                                                                            size: 0
                                                                                        }));
                                                                                        setChunks(initial);
                                                                                    }

                                                                                    // Update each chunk status & auto-download when ready
                                                                                    if (totalChunks > 0) {
                                                                                        const updates: Partial<ChunkStatus>[] = [];
                                                                                        for (let ci = 0; ci < totalChunks; ci++) {
                                                                                            const cStatus = data?.[`chunk_${ci}_status`] || 'pending';
                                                                                            const cProgress = data?.[`chunk_${ci}_progress`] || 0;
                                                                                            const cUrl = data?.[`chunk_${ci}_url`] || null;

                                                                                            if (cStatus === 'complete') { chunksDone++; totalChunkProgress += 100; }
                                                                                            else if (cStatus === 'failed') { chunksFailed++; }
                                                                                            else if (cStatus === 'encoding') { chunksEncoding++; totalChunkProgress += cProgress; }

                                                                                            updates.push({
                                                                                                serverStatus: cStatus as any,
                                                                                                serverProgress: cProgress,
                                                                                                url: cUrl
                                                                                            });

                                                                                            // Auto-download completed chunks
                                                                                            if (cStatus === 'complete' && cUrl) {
                                                                                                const currentChunk = chunksRef.current[ci];
                                                                                                if (currentChunk && currentChunk.localStatus === 'waiting') {
                                                                                                    downloadChunkToFS(ci, cUrl);
                                                                                                }
                                                                                            }
                                                                                        }

                                                                                        setChunks(prev => prev.map((c, i) => ({
                                                                                            ...c,
                                                                                            ...(updates[i] || {}),
                                                                                            // Don't overwrite local download state
                                                                                            localStatus: c.localStatus,
                                                                                            localProgress: c.localProgress,
                                                                                            size: c.size
                                                                                        })));
                                                                                    }

                                                                                    const phase = data?.status || '';
                                                                                    let computedProgress = data?.progress || 0;
                                                                                    let statusMessage = data?.currentStep || `Processing... ${computedProgress}%`;

                                                                                    if (totalChunks > 1 && phase !== 'complete') {
                                                                                        if (chunksDone > 0 || chunksEncoding > 0) {
                                                                                            const avgChunkProgress = totalChunks > 0 ? totalChunkProgress / totalChunks : 0;
                                                                                            computedProgress = 15 + Math.round(avgChunkProgress * 0.65);
                                                                                            statusMessage = `⚙️ ${chunksDone}/${totalChunks} ✅${chunksEncoding > 0 ? ` | ${chunksEncoding} en cours` : ''}`;
                                                                                        }
                                                                                    }

                                                                                    setServerProgress({ progress: computedProgress, message: statusMessage });

                                                                                    // All chunks failed
                                                                                    if (chunksFailed > 0 && phase !== 'failed' && phase !== 'complete') {
                                                                                        const settled = chunksDone + chunksFailed;
                                                                                        if (settled >= totalChunks) {
                                                                                            unsubscribe();
                                                                                            reject(new Error(`${chunksFailed}/${totalChunks} chunk(s) échoué(s)`));
                                                                                            return;
                                                                                        }
                                                                                    }

                                                                                    // All chunks complete on server → downloads will auto-trigger
                                                                                    // Client-side merge will be triggered by the useEffect when all are downloaded
                                                                                    if (chunksDone === totalChunks && totalChunks > 0) {
                                                                                        unsubscribe();
                                                                                        resolve(); // Don't wait for server merge!
                                                                                    }

                                                                                    if (data?.status === 'failed') {
                                                                                        unsubscribe();
                                                                                        reject(new Error(data.error || 'Export failed'));
                                                                                    }
                                                                                });
                                                                            });


                                                                            fetch(`${EXPORT_SERVICE_URL}/burn-subtitles-parallel`, {
                                                                                method: 'POST',
                                                                                headers: { 'Content-Type': 'application/json' },
                                                                                body: JSON.stringify({
                                                                                    jobId,
                                                                                    videoUrl: cloudVideoUrl,
                                                                                    subtitleUrl: r2SubtitleUrl,
                                                                                    userId: 'constance',
                                                                                    projectId: 'constance'
                                                                                })
                                                                            }).catch(() => { }); // fire-and-forget

                                                                            await firestoreResultPromise;
                                                                            // Client-side merge happens via the auto-trigger useEffect
                                                                            // serverVideoUrl will be set by performClientMerge
                                                                            setLogs(prev => [...prev, `☁️ Cloud: ✅ Tous les chunks prêts! Assemblage local...`]);
                                                                        } catch (e: any) {
                                                                            console.error('Server export failed:', e);
                                                                            setLogs(prev => [...prev, `☁️ Cloud: ❌ ${e.message}`]);
                                                                            setServerProgress(null);
                                                                        } finally {
                                                                            serverDone = true;
                                                                            checkAllDone();
                                                                        }
                                                                    };

                                                                    // ═══════════════════════════════════════
                                                                    // 2. WEB EXPORT (🌐 Browser → MP4)
                                                                    // ═══════════════════════════════════════
                                                                    const webExport = async () => {
                                                                        try {
                                                                            setLogs(prev => [...prev, `🌐 Web: Lancé (WebCodecs)`]);
                                                                            setWebProgress({ progress: 0, message: 'Préparation WebCodecs...' });

                                                                            // Fetch subtitle content
                                                                            const subResponse = await fetch(subtitleUrl!);
                                                                            const vttContent = await subResponse.text();

                                                                            // Use the video URL (prefer blob for local, or R2 URL)
                                                                            const videoSource = url;

                                                                            // Auto-calculate chunk count: ~2 min per chunk to stay under memory limits
                                                                            const videoEl = document.querySelector('video') as HTMLVideoElement | null;
                                                                            const videoDuration = videoEl?.duration || 300;
                                                                            const numChunks = Math.max(1, Math.ceil(videoDuration / 120)); // 1 chunk per 2 min
                                                                            setLogs(prev => [...prev, `🌐 Web: ${videoDuration.toFixed(0)}s → ${numChunks} chunk(s)`]);

                                                                            const blob = await burnSubtitlesSequential(
                                                                                videoSource,
                                                                                vttContent,
                                                                                numChunks,
                                                                                (p) => {
                                                                                    if (cancelBurnRef.current) return;
                                                                                    setWebProgress({
                                                                                        progress: Math.round(p.progress),
                                                                                        message: p.message,
                                                                                        fps: p.fps
                                                                                    });
                                                                                }
                                                                            );

                                                                            if (cancelBurnRef.current) return;

                                                                            const webUrl = URL.createObjectURL(blob);
                                                                            setWebVideoUrl(webUrl);
                                                                            setWebProgress(null);
                                                                            setLogs(prev => [...prev, `🌐 Web: ✅ Terminé! (${(blob.size / 1024 / 1024).toFixed(1)} MB)`]);

                                                                            // Auto-download web result
                                                                            const a = document.createElement('a');
                                                                            a.href = webUrl;
                                                                            a.download = 'video_web.mp4';
                                                                            a.click();
                                                                        } catch (e: any) {
                                                                            console.error('Web export failed:', e);
                                                                            setLogs(prev => [...prev, `🌐 Web: ❌ ${e.message}`]);
                                                                            setWebProgress(null);
                                                                        } finally {
                                                                            webDone = true;
                                                                            checkAllDone();
                                                                        }
                                                                    };

                                                                    // Fire both simultaneously!
                                                                    serverExport();
                                                                    webExport();
                                                                }}
                                                                className={`flex items-center gap-1 text-xs font-bold px-3 py-1 uppercase rounded-sm transition-colors ${cloudReady
                                                                    ? 'bg-gradient-to-r from-purple-600 to-blue-500 hover:from-purple-700 hover:to-blue-600 text-white'
                                                                    : 'bg-gray-500 text-gray-300 cursor-not-allowed'
                                                                    } disabled:opacity-50`}
                                                                title={cloudReady ? "Export Cloud (auto-parallel)" : "⏳ Waiting for R2..."}
                                                            >
                                                                {isProcessing ? <Loader2 size={12} className="animate-spin" /> : <Flame size={12} className="fill-current" />}
                                                                EXPORT
                                                            </button>
                                                        );
                                                    })()}
                                                </div>
                                                <a href={url} target="_blank" className="text-xs font-black hover:underline uppercase bg-black text-white px-4 py-2 rounded-sm flex items-center gap-2">
                                                    Download MP4 &rarr;
                                                </a>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}


                            {/* CHUNK MANAGER UI */}
                            {chunks.length > 0 && (
                                <div className="md:col-span-2 border-2 border-gray-800 bg-gray-50 p-4">
                                    <div className="flex justify-between items-center mb-3">
                                        <h4 className="font-black uppercase text-sm flex items-center gap-2">
                                            📦 Chunk Manager ({chunks.filter(c => c.localStatus === 'downloaded').length}/{chunks.length})
                                        </h4>
                                        <div className="flex items-center gap-2 text-xs">
                                            {isMerging && (
                                                <span className="bg-purple-600 text-white px-2 py-1 rounded-sm font-bold animate-pulse flex items-center gap-1">
                                                    <Loader2 size={10} className="animate-spin" />
                                                    Assemblage...
                                                </span>
                                            )}
                                            {!isMerging && chunks.every(c => c.localStatus === 'downloaded') && !serverVideoUrl && (
                                                <span className="bg-green-600 text-white px-2 py-1 rounded-sm font-bold">
                                                    ✓ Prêt à assembler
                                                </span>
                                            )}
                                            <span className="text-gray-500 font-bold">
                                                {(chunks.reduce((acc, c) => acc + c.size, 0) / 1024 / 1024).toFixed(0)} MB total
                                            </span>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-10 gap-1">
                                        {chunks.map((chunk, idx) => {
                                            const serverIcon = chunk.serverStatus === 'complete' ? '✅' :
                                                chunk.serverStatus === 'encoding' ? '⚙️' :
                                                    chunk.serverStatus === 'failed' ? '❌' :
                                                        chunk.serverStatus === 'downloading' ? '⬇️' : '⏳';

                                            const localBg = chunk.localStatus === 'downloaded' ? 'bg-green-100 border-green-500' :
                                                chunk.localStatus === 'downloading' ? 'bg-blue-100 border-blue-500' :
                                                    chunk.localStatus === 'failed' ? 'bg-red-100 border-red-500' :
                                                        'bg-white border-gray-300';

                                            return (
                                                <div
                                                    key={idx}
                                                    onClick={() => retryChunkDownload(idx)}
                                                    className={`relative border-2 ${localBg} p-1 text-center text-xs font-bold rounded-sm overflow-hidden cursor-pointer hover:opacity-80 active:scale-95 transition-all`}
                                                    title={`Chunk ${idx} — Cliquer pour relancer\nServer: ${chunk.serverStatus} (${chunk.serverProgress}%)\nLocal: ${chunk.localStatus} (${chunk.localProgress}%)\n${chunk.size > 0 ? (chunk.size / 1024 / 1024).toFixed(1) + ' MB' : ''}`}
                                                >
                                                    {/* Download progress bar */}
                                                    {chunk.localStatus === 'downloading' && (
                                                        <div
                                                            className="absolute bottom-0 left-0 h-1 bg-blue-500 transition-all"
                                                            style={{ width: `${chunk.localProgress}%` }}
                                                        />
                                                    )}
                                                    <div className="text-[10px] leading-tight">
                                                        {serverIcon}
                                                    </div>
                                                    <div className="text-[9px] text-gray-600">
                                                        {idx}
                                                    </div>
                                                    {/* Failed overlay */}
                                                    {(chunk.localStatus === 'failed' || chunk.serverStatus === 'failed') && (
                                                        <div className="absolute inset-0 bg-red-500/60 text-white flex items-center justify-center">
                                                            <RefreshCw size={10} />
                                                        </div>
                                                    )}
                                                    {/* Downloading spinner */}
                                                    {chunk.localStatus === 'downloading' && (
                                                        <div className="absolute top-0 right-0">
                                                            <Loader2 size={8} className="animate-spin text-blue-600" />
                                                        </div>
                                                    )}
                                                    {/* Downloaded check */}
                                                    {chunk.localStatus === 'downloaded' && (
                                                        <div className="absolute top-0 right-0 text-[8px]">💾</div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="mt-2 flex gap-4 text-[10px] text-gray-500 font-bold uppercase">
                                        <span>⏳ En attente</span>
                                        <span>⚙️ Encodage</span>
                                        <span>✅ Prêt</span>
                                        <span>💾 Téléchargé</span>
                                        <span className="text-red-500">❌ Échec (cliquer pour retry)</span>
                                    </div>
                                </div>
                            )}

                            {/* DUAL EXPORT RESULTS */}
                            {(serverVideoUrl || webVideoUrl) && (
                                <div data-burned-result className="md:col-span-2 space-y-4">
                                    {/* Server Result */}
                                    {serverVideoUrl && (
                                        <div className="border-4 border-purple-600 p-4 bg-purple-50">
                                            <h4 className="font-black uppercase mb-4 flex items-center gap-2 text-purple-700">
                                                <Flame size={20} className="fill-purple-600" />
                                                ☁️ Cloud Export (MP4)
                                            </h4>
                                            <video controls src={serverVideoUrl} className="w-full border-4 border-purple-600 shadow-[8px_8px_0px_0px_rgba(147,51,234,1)]" />
                                            <div className="flex justify-between items-center mt-4">
                                                <span className="text-xs font-bold bg-purple-600 text-white px-2 py-1 uppercase rounded-sm">
                                                    ✓ Cloud FFmpeg
                                                </span>
                                                <a href={serverVideoUrl} download="video_cloud.mp4" className="text-xs font-black hover:underline uppercase bg-purple-700 text-white px-4 py-2 rounded-sm flex items-center gap-2">
                                                    Télécharger MP4 &rarr;
                                                </a>
                                            </div>
                                        </div>
                                    )}

                                    {/* Web Result */}
                                    {webVideoUrl && (
                                        <div className="border-4 border-blue-600 p-4 bg-blue-50">
                                            <h4 className="font-black uppercase mb-4 flex items-center gap-2 text-blue-700">
                                                <Zap size={20} className="fill-blue-600" />
                                                🌐 Web Export (MP4)
                                            </h4>
                                            <video controls src={webVideoUrl} className="w-full border-4 border-blue-600 shadow-[8px_8px_0px_0px_rgba(37,99,235,1)]" />
                                            <div className="flex justify-between items-center mt-4">
                                                <span className="text-xs font-bold bg-blue-600 text-white px-2 py-1 uppercase rounded-sm">
                                                    ✓ WebCodecs Browser
                                                </span>
                                                <a href={webVideoUrl} download="video_web.mp4" className="text-xs font-black hover:underline uppercase bg-blue-700 text-white px-4 py-2 rounded-sm flex items-center gap-2">
                                                    Télécharger MP4 &rarr;
                                                </a>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                        </div>

                        {/* ERREUR */}
                        {error && (
                            <div className="mt-8 border-2 border-red-600 bg-red-50 text-red-600 p-4 font-bold text-left">
                                ERROR: {error}
                            </div>
                        )}
                    </div>

                    {/* LOGS TERMINAL STYLE */}
                    <div className="border border-black bg-white p-4 font-mono text-xs max-h-60 overflow-y-auto">
                        <div className="font-bold border-b border-black mb-2 pb-1 uppercase">Process Log Output</div>
                        {logs.map((log, i) => (
                            <div key={i} className="mb-0.5 truncate">{log}</div>
                        ))}
                        {logs.length === 0 && <span className="opacity-30">Waiting for input...</span>}
                    </div>

                </div>
            </div>

            {/* RIGHT SIDEBAR: Subtitle Editor */}
            {showSubEditor && subtitleSegments.length > 0 && (
                <div className="w-[380px] border-l-4 border-black bg-gray-50 flex flex-col overflow-hidden">
                    {/* Header */}
                    <div className="border-b-2 border-black p-4 bg-white">
                        <div className="flex items-center justify-between">
                            <h3 className="font-black uppercase text-sm flex items-center gap-2">
                                <Pencil size={14} />
                                Subtitle Editor ({subtitleSegments.length})
                            </h3>
                            <div className="flex gap-2">
                                <button
                                    onClick={handleExportSrt}
                                    className="text-xs font-bold bg-black text-white px-2 py-1 flex items-center gap-1 hover:bg-gray-800"
                                    title="Export SRT"
                                >
                                    <Download size={10} /> SRT
                                </button>
                                <button
                                    onClick={() => setShowSubEditor(false)}
                                    className="text-xs font-bold bg-gray-300 text-black px-2 py-1 hover:bg-gray-400"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Search Bar */}
                    <div className="border-b border-gray-300 p-3 bg-white space-y-2">
                        <div className="flex items-center gap-1">
                            <Search size={12} className="text-gray-400 shrink-0" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search subtitles..."
                                className="flex-1 text-xs font-mono border border-gray-300 px-2 py-1 focus:outline-none focus:border-black"
                            />
                            <button
                                onClick={() => setShowReplace(!showReplace)}
                                className={`text-[10px] font-bold px-1.5 py-1 border ${showReplace ? 'bg-black text-white border-black' : 'bg-gray-100 text-gray-500 border-gray-300 hover:bg-gray-200'}`}
                                title="Find & Replace"
                            >
                                A→B
                            </button>
                            {searchQuery && (
                                <span className="text-[10px] font-bold text-gray-400 shrink-0">
                                    {subtitleSegments.filter(s => s.text.toLowerCase().includes(searchQuery.toLowerCase())).length} hits
                                </span>
                            )}
                        </div>
                        {showReplace && searchQuery && (
                            <div className="flex items-center gap-1">
                                <span className="text-[10px] text-gray-400 shrink-0">→</span>
                                <input
                                    type="text"
                                    value={replaceQuery}
                                    onChange={(e) => setReplaceQuery(e.target.value)}
                                    placeholder="Replace with..."
                                    className="flex-1 text-xs font-mono border border-gray-300 px-2 py-1 focus:outline-none focus:border-black"
                                />
                                <button
                                    onClick={() => {
                                        if (!searchQuery) return;
                                        const regex = new RegExp(searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
                                        setSubtitleSegments(prev => {
                                            const updated = prev.map(s => ({
                                                ...s,
                                                text: s.text.replace(regex, replaceQuery)
                                            }));
                                            regenerateVtt(updated);
                                            return updated;
                                        });
                                        setSearchQuery('');
                                        setReplaceQuery('');
                                    }}
                                    className="text-[10px] font-bold bg-purple-600 text-white px-2 py-1 hover:bg-purple-700 shrink-0"
                                >
                                    Replace All
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Subtitle List */}
                    <div ref={subListRef} className="flex-1 overflow-y-auto">
                        {subtitleSegments
                            .map((seg, i) => ({ seg, i }))
                            .filter(({ seg }) => !searchQuery || seg.text.toLowerCase().includes(searchQuery.toLowerCase()))
                            .map(({ seg, i }) => {
                                // Highlight search matches
                                const highlightText = (text: string) => {
                                    if (!searchQuery) return text;
                                    const parts = text.split(new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
                                    return parts.map((part, j) =>
                                        part.toLowerCase() === searchQuery.toLowerCase()
                                            ? <mark key={j} className="bg-yellow-300 px-0.5">{part}</mark>
                                            : part
                                    );
                                };
                                return (
                                    <div
                                        key={i}
                                        className={`border-b border-gray-200 p-3 hover:bg-white transition-colors cursor-pointer ${activeSubIndex === i ? 'bg-green-50 border-l-4 border-l-green-500' :
                                            editingSubId === i ? 'bg-yellow-50 border-l-4 border-l-yellow-500' :
                                                searchQuery && seg.text.toLowerCase().includes(searchQuery.toLowerCase()) ? 'bg-blue-50 border-l-4 border-l-blue-400' : ''
                                            }`}
                                        onClick={() => handleSeekTo(seg.start)}
                                    >
                                        {/* Index + Timing */}
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-[10px] font-bold text-gray-400 uppercase">#{i + 1}</span>
                                            <div className="flex items-center gap-1">
                                                <Clock size={10} className="text-gray-400" />
                                                <input
                                                    type="number"
                                                    step="0.1"
                                                    value={seg.start.toFixed(1)}
                                                    onChange={(e) => handleSubTimingChange(i, 'start', parseFloat(e.target.value) || 0)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="w-14 text-[10px] font-mono bg-gray-100 border border-gray-300 px-1 py-0.5 text-center"
                                                />
                                                <span className="text-[10px] text-gray-400">→</span>
                                                <input
                                                    type="number"
                                                    step="0.1"
                                                    value={seg.end.toFixed(1)}
                                                    onChange={(e) => handleSubTimingChange(i, 'end', parseFloat(e.target.value) || 0)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="w-14 text-[10px] font-mono bg-gray-100 border border-gray-300 px-1 py-0.5 text-center"
                                                />
                                            </div>
                                        </div>

                                        {/* Text */}
                                        {editingSubId === i ? (
                                            <textarea
                                                value={seg.text}
                                                onChange={(e) => handleSubTextChange(i, e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                                onBlur={() => setEditingSubId(null)}
                                                autoFocus
                                                rows={2}
                                                className="w-full text-xs font-mono border-2 border-yellow-400 bg-yellow-50 p-1 resize-none focus:outline-none"
                                            />
                                        ) : (
                                            <div
                                                className="text-xs font-mono leading-relaxed hover:bg-gray-100 p-1 rounded cursor-text"
                                                onClick={(e) => { e.stopPropagation(); setEditingSubId(i); }}
                                            >
                                                {highlightText(seg.text)}
                                            </div>
                                        )}

                                        {/* Actions */}
                                        <div className="flex justify-end gap-1 mt-1">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleAddSub(i); }}
                                                className="text-[10px] text-gray-400 hover:text-green-600 p-0.5"
                                                title="Add subtitle after"
                                            >
                                                <Plus size={10} />
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleDeleteSub(i); }}
                                                className="text-[10px] text-gray-400 hover:text-red-600 p-0.5"
                                                title="Delete subtitle"
                                            >
                                                <Trash2 size={10} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                    </div>

                    {/* Footer */}
                    <div className="border-t-2 border-black p-3 bg-white">
                        <button
                            onClick={() => handleAddSub(subtitleSegments.length - 1)}
                            className="w-full text-xs font-bold bg-gray-100 hover:bg-gray-200 border border-gray-300 py-2 flex items-center justify-center gap-1"
                        >
                            <Plus size={12} /> Add Subtitle
                        </button>
                    </div>
                </div>
            )}

            {/* Toggle button when editor is hidden */}
            {subtitleSegments.length > 0 && !showSubEditor && (
                <button
                    onClick={() => setShowSubEditor(true)}
                    className="fixed right-12 top-1/2 -translate-y-1/2 bg-black text-white px-2 py-4 font-bold text-xs uppercase tracking-widest hover:bg-gray-800 border-2 border-black"
                    style={{ writingMode: 'vertical-rl' }}
                >
                    <Pencil size={12} className="mb-2" /> Subtitles ({subtitleSegments.length})
                </button>
            )}
        </div>
    );
};
